import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Shield, Award, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

// Customer reviews
const mockReviews = [
  {
    name: 'Sarah Thompson',
    location: 'Solihull',
    rating: 5,
    comment: 'Excellent service from start to finish. They repaired our pitched roof quickly and professionally. Highly recommend!'
  },
  {
    name: 'David Williams',
    location: 'Birmingham',
    rating: 5,
    comment: 'Called them for an emergency leak and they were here within 2 hours. Outstanding work and very reasonably priced.'
  },
  {
    name: 'Emma Roberts',
    location: 'Solihull',
    rating: 5,
    comment: 'Had our loft conversion done by The Roofing Company. Fantastic workmanship and the team was very professional throughout.'
  },
  {
    name: 'Michael Brown',
    location: 'West Midlands',
    rating: 5,
    comment: 'Replaced our entire flat roof. The quality of work is exceptional and they cleaned up perfectly after themselves.'
  },
  {
    name: 'Jennifer Clarke',
    location: 'Solihull',
    rating: 5,
    comment: 'Very impressed with their attention to detail. Our new pitched roof looks amazing and was completed on schedule.'
  },
  {
    name: 'James Anderson',
    location: 'Birmingham',
    rating: 5,
    comment: 'Brilliant emergency service! Fixed our roof leak in terrible weather. True professionals with over 20 years experience shows.'
  }
];

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Client-side validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setIsSubmitting(false);
      setSubmitStatus('validation');
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        const errorData = await response.json();
        setIsSubmitting(false);
        if (response.status === 400) {
          setSubmitStatus('validation');
        } else {
          setSubmitStatus('error');
        }
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Emergency Banner */}
      <div className="bg-teal-600 text-white py-3 px-4 text-center font-medium">
        <div className="container mx-auto flex items-center justify-center gap-2 flex-wrap">
          <Phone className="w-5 h-5" />
          <span>24/7 Emergency Call-Outs Available</span>
          <a href="tel:07438248882" className="underline font-bold hover:text-teal-100 transition-colors">
            07438 248882
          </a>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/0b19pkma_The%20Roofing%20company_20251121_221532_0000.png" 
                alt="The Roofing Company" 
                className="h-16 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Services</a>
              <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">About</a>
              <a href="#reviews" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Reviews</a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Contact</a>
              <a href="tel:07438248882">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </nav>
            <a href="tel:07438248882" className="md:hidden">
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white">
                <Phone className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1660490483750-855f80aa4cf7)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/95 to-gray-900/80" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              The Roofing Company
            </h1>
            <h2 className="text-2xl md:text-3xl mb-6 text-gray-100 font-semibold">
              Roofing Experts Serving Solihull, Warwick, Leamington Spa & Surrounding Areas
            </h2>
            <p className="text-lg md:text-xl mb-8 text-gray-200">
              From emergency roof repairs to complete re-roofs, we cover Solihull, Warwick, Leamington Spa, Dorridge, Knowle, Henley-in-Arden and Stratford-upon-Avon. Fully insured, reliable and competitively priced.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button 
                size="lg" 
                className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                onClick={scrollToContact}
              >
                Get Your FREE Quote Today
              </Button>
              <a href="tel:07438248882">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 bg-white hover:bg-gray-100 text-gray-900 font-semibold border-2 border-white shadow-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: 07438 248882
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap gap-6 text-sm items-center">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-400" />
                <span>Fully Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-teal-400" />
                <span>20+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-400" />
                <span>24/7 Emergency Service</span>
              </div>
              <a 
                href="https://veritradeuk.com/verify/VT-2D8BF2DF" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors border border-white/20"
              >
                <Shield className="w-5 h-5 text-teal-400" />
                <span className="font-medium">VeriTrade Verified</span>
              </a>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white opacity-70" />
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive roofing solutions for residential and commercial properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Pitched Roofing',
                description: 'Expert installation and repair of traditional pitched roofs with premium quality tiles and materials.',
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/hkznmx2s_20230301_144913.jpg',
                alt: 'Professional slate tile installation in Solihull by The Roofing Company'
              },
              {
                title: 'Flat Roofing',
                description: 'High-performance flat roofing systems with long-lasting, weather-resistant materials.',
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/6e9chjuh_20250926_112542.jpg',
                alt: 'Professional flat roof installation in Leamington Spa by The Roofing Company'
              },
              {
                title: 'Roof Construction',
                description: 'Complete roof construction from timber framing to final finish for new builds and extensions.',
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/6bo5k9lb_IMG_20221117_152738.jpg',
                alt: 'New roof construction timber framing in Solihull by The Roofing Company'
              },
              {
                title: 'Loft Conversions',
                description: 'Professional loft conversions to add valuable living space to your property.',
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/rzwsqh7m_20240701_143649.jpg',
                alt: 'Loft conversion construction in Warwick by The Roofing Company'
              },
              {
                title: 'Emergency Repairs',
                description: '24/7 rapid response for storm damage, leaks, and urgent roofing emergencies.',
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/fiyzt2j2_20240627_121107.jpg',
                alt: 'Emergency roof repair and scaffolding in Warwick by The Roofing Company'
              },
              {
                title: 'Roof Repairs',
                description: 'Expert repairs for damaged tiles, slates, flashings, guttering and all roofing issues.',
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/dc39p03z_IMG_20230907_131114_755.jpg',
                alt: 'Professional roof repair work in Dorridge by The Roofing Company'
              }
            ].map((service, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent" />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Why Choose The Roofing Company?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your trusted roofing partner with a proven track record of excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">20+ Years Experience</h3>
              <p className="text-gray-600">Two decades of expert roofing services across the West Midlands</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Fully Insured</h3>
              <p className="text-gray-600">Complete peace of mind with comprehensive insurance coverage</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">24/7 Emergency</h3>
              <p className="text-gray-600">Round-the-clock emergency response when you need it most</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Quality Guaranteed</h3>
              <p className="text-gray-600">All work backed by our workmanship guarantee</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-xl shadow-lg max-w-3xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Ready to Start Your Roofing Project?</h3>
              <p className="text-lg mb-6 text-teal-50">Get your free, no-obligation quote today. Most quotes provided within 24 hours.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:07438248882">
                  <Button size="lg" className="bg-white hover:bg-gray-100 text-teal-700 text-lg px-8 py-6 w-full sm:w-auto font-semibold">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 07438 248882
                  </Button>
                </a>
                <Button 
                  size="lg" 
                  onClick={scrollToContact}
                  className="bg-gray-900 hover:bg-gray-800 text-white text-lg px-8 py-6 w-full sm:w-auto font-semibold"
                >
                  Get Free Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Trusted Roofing Experts Since 2004</h2>
              <p className="text-lg text-gray-600 mb-6">
                With over 20 years of experience, The Roofing Company has built a reputation as one of the most trusted roofing contractors in Solihull and Warwickshire. We pride ourselves on delivering exceptional quality workmanship and outstanding customer service on every project.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From simple repairs to complete re-roofs, loft conversions to emergency call-outs, our skilled team handles every job with professionalism and attention to detail. We only use premium materials and proven techniques to ensure your roof stands the test of time.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-4xl font-bold text-teal-600 mb-2">1000+</div>
                  <div className="text-gray-600">Projects Completed</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
                  <div className="text-gray-600">Customer Satisfaction</div>
                </div>
              </div>
              <div className="mt-8">
                <Button 
                  size="lg" 
                  onClick={scrollToContact}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 font-semibold"
                >
                  Start Your Project Today
                </Button>
              </div>
            </div>
            <div className="relative h-96 md:h-full min-h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/hkznmx2s_20230301_144913.jpg" 
                alt="Professional roofing craftmanship by The Roofing Company in Solihull and Warwickshire"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 right-6 bg-teal-600 text-white px-6 py-3 rounded-lg shadow-lg">
                <div className="text-2xl font-bold">20+</div>
                <div className="text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Cover Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Areas We Cover</h2>
            <p className="text-lg text-gray-600 mb-12">
              The Roofing Company provides professional roofing services across Solihull and Warwickshire. 
              If you live in or around any of the areas below, we can help with repairs, re-roofs and emergency call-outs.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {[
                'Solihull',
                'Warwick',
                'Leamington Spa',
                'Dorridge',
                'Knowle',
                'Henley-in-Arden',
                'Stratford-upon-Avon',
                'Surrounding Areas'
              ].map((area, index) => (
                <div key={index} className="bg-white p-4 rounded-lg hover:bg-teal-50 hover:shadow-md transition-all border-2 border-gray-100 hover:border-teal-200">
                  <p className="font-semibold text-gray-900 flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4 text-teal-600" />
                    {area}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="bg-teal-50 border-2 border-teal-200 p-6 rounded-lg">
              <p className="text-gray-700 font-medium">
                <Phone className="w-5 h-5 inline mr-2 text-teal-600" />
                Not sure if we cover your area? Get in touch and we'll let you know within minutes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Recent Projects</h2>
            <p className="text-xl text-gray-600">See the quality of our workmanship for yourself</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/hkznmx2s_20230301_144913.jpg',
                title: 'Premium Slate Installation',
                location: 'Solihull'
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/6e9chjuh_20250926_112542.jpg',
                title: 'Modern Flat Roof System',
                location: 'Leamington Spa'
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/6bo5k9lb_IMG_20221117_152738.jpg',
                title: 'New Build Roof Construction',
                location: 'Warwick'
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/rzwsqh7m_20240701_143649.jpg',
                title: 'Loft Conversion Project',
                location: 'Dorridge'
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/fiyzt2j2_20240627_121107.jpg',
                title: 'Complete Re-Roof',
                location: 'Knowle'
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_243c419f-fb94-4209-ad17-6a5404b4ffbb/artifacts/dc39p03z_IMG_20230907_131114_755.jpg',
                title: 'Extension Roof Build',
                location: 'Stratford-upon-Avon'
              }
            ].map((project, index) => (
              <div key={index} className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="aspect-w-4 aspect-h-3 relative h-80">
                  <img 
                    src={project.image} 
                    alt={`${project.title} in ${project.location} by The Roofing Company`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-xl font-bold mb-1">{project.title}</h3>
                    <p className="flex items-center gap-2 text-teal-300">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg mb-6">Want to see your project completed to this standard?</p>
            <Button 
              size="lg" 
              onClick={scrollToContact}
              className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 font-semibold"
            >
              Request Your Free Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Customer Reviews</h2>
            <p className="text-xl text-gray-600">See what our satisfied customers have to say</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockReviews.map((review, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{review.comment}"</p>
                  <div className="font-semibold text-gray-900">{review.name}</div>
                  <div className="text-sm text-gray-500">{review.location}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">Get In Touch</h2>
              <p className="text-xl text-gray-600">Request a free quote or ask us anything</p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                      placeholder="07XXX XXXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full"
                      placeholder="Tell us about your roofing needs..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                  {submitStatus === 'success' && (
                    <p className="text-green-600 text-center font-medium">Message sent successfully! We'll be in touch soon.</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-red-600 text-center font-medium">Something went wrong. Please try again or call us directly.</p>
                  )}
                  {submitStatus === 'validation' && (
                    <p className="text-amber-600 text-center font-medium">Please fill in all fields before sending your message.</p>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">Contact Information</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-teal-100 p-3 rounded-lg">
                        <Phone className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Phone</div>
                        <a href="tel:07438248882" className="text-gray-600 hover:text-teal-600 transition-colors block">
                          07438 248882
                        </a>
                        <a href="tel:01212447004" className="text-gray-600 hover:text-teal-600 transition-colors block">
                          0121 244 7004
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-teal-100 p-3 rounded-lg">
                        <Mail className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Email</div>
                        <a href="mailto:theroofingcompanyco@gmail.com" className="text-gray-600 hover:text-teal-600 transition-colors break-all">
                          theroofingcompanyco@gmail.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-teal-100 p-3 rounded-lg">
                        <MapPin className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Address</div>
                        <p className="text-gray-600">
                          Vulcan Rd<br />
                          Solihull<br />
                          B91 2JY<br />
                          West Midlands, UK
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 p-6 rounded-lg border-2 border-teal-200">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-teal-600" />
                    Emergency Services
                  </h4>
                  <p className="text-gray-600">Available 24/7 for urgent roofing repairs and emergency call-outs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">The Roofing Company</h3>
              <p className="text-gray-400 mb-4">
                Professional roofing services in Solihull and across the West Midlands for over 20 years.
              </p>
              <a 
                href="https://veritradeuk.com/verify/VT-2D8BF2DF" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
              >
                <Shield className="w-4 h-4" />
                <span>Verified on VeriTrade</span>
              </a>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Pitched Roofing</li>
                <li>Flat Roofing</li>
                <li>Roof Construction</li>
                <li>Loft Conversions</li>
                <li>Emergency Repairs</li>
                <li>Roof Repairs</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Vulcan Rd, Solihull B91 2JY</li>
                <li>
                  <a href="tel:07438248882" className="hover:text-teal-400 transition-colors">
                    07438 248882
                  </a>
                </li>
                <li>
                  <a href="tel:01212447004" className="hover:text-teal-400 transition-colors">
                    0121 244 7004
                  </a>
                </li>
                <li>
                  <a href="mailto:theroofingcompanyco@gmail.com" className="hover:text-teal-400 transition-colors break-all">
                    theroofingcompanyco@gmail.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} The Roofing Company. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;